import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchPage } from "@/api/cms";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { renderMarkdown } from "@/lib/markdown";
import { NotFound } from "@/pages/NotFound";
import { useMarket } from "../MarketProvider";
import { useV3 } from "../useV3";

/* A page an admin wrote: /p/about, /p/terms, and so on.
 *
 * A page that is not PUBLISHED is not a page. The worker filters on
 * `published=true`, so an unpublished draft reaches this screen as nothing
 * found, and nothing found renders the site's 404 -- not an empty article and
 * not "coming soon". The footer only links pages that exist, so arriving here
 * on a miss means a typed or stale URL, which is exactly what a 404 is for.
 *
 * The body is Markdown, rendered by the same helper the rest of the site uses
 * -- one sanitiser, one set of rules about what an author may emit.
 */
export function ContentPage() {
  const { slug = "" } = useParams();
  const { locale } = useV3();
  const { market, loading: marketLoading } = useMarket();
  const [page, setPage] = useState<{ title: string; body: string; meta_description: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!market) return;
    const ctrl = new AbortController();
    let live = true;
    setLoading(true);
    fetchPage(slug, locale, market.slug, ctrl.signal)
      .then((p) => { if (live) setPage(p); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; ctrl.abort(); };
  }, [slug, locale, market]);

  useDocumentMeta((d) => ({
    title: page?.title || d.meta.documentTitle,
    description: page?.meta_description || d.meta.documentDescription,
  }));

  if (loading || marketLoading) {
    return (
      <section className="sq-section sq-gutter" aria-busy="true">
        <div className="sq-skeleton" style={{ height: 32, width: "40%", marginBottom: 16 }} />
        <div className="sq-skeleton" style={{ height: 14, width: "90%", marginBottom: 8 }} />
        <div className="sq-skeleton" style={{ height: 14, width: "80%" }} />
      </section>
    );
  }
  if (!page) return <NotFound />;

  return (
    <article className="sq-section sq-gutter">
      <h1 className="sq-page-title">{page.title}</h1>
      <div className="sq-detail__body">{renderMarkdown(page.body)}</div>
    </article>
  );
}
